import React, { useMemo } from "react";
import {
  EuiOverlayMask,
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiText,
  EuiSpacer,
  EuiButton,
  EuiButtonEmpty,
} from "@elastic/eui";
import { FiClock } from "react-icons/fi";

const WORKSPACE_COPY = {
  practice: {
    title: "Pick your practice flow",
    prompt:
      "You already have an ongoing practice chat. Do you want to jump back into it or launch a fresh set?",
    continueLabel: "Go to last practice chat",
    newLabel: "Start new practice set",
  },
  study: {
    title: "Plan your study session",
    prompt:
      "You already have a study planning chat saved. Would you like to keep going or spin up a new plan?",
    continueLabel: "Resume last study plan",
    newLabel: "Start new study plan",
  },
  default: {
    title: "Continue where you left off?",
    prompt:
      "There's an existing chat for this workspace. Choose whether to continue it or start fresh.",
    continueLabel: "Continue previous chat",
    newLabel: "Start new chat",
  },
};

const WorkspaceChoiceModal = ({
  isOpen,
  workspaceMode,
  lastSession,
  onContinue,
  onStartNew,
  onClose,
}) => {
  const copy = useMemo(() => {
    return WORKSPACE_COPY[workspaceMode] || WORKSPACE_COPY.default;
  }, [workspaceMode]);

  const sessionSummary = useMemo(() => {
    if (!lastSession) {
      return null;
    }
    const sessionName = lastSession.name || lastSession.id || "Previous chat";
    const rawTimestamp =
      lastSession.last_message_date ||
      (Array.isArray(lastSession.chat_history) &&
      lastSession.chat_history.length > 0
        ? lastSession.chat_history[lastSession.chat_history.length - 1]
            ?.date_and_time
        : null) ||
      lastSession.date_and_time ||
      null;
    const timestamp = rawTimestamp ? new Date(rawTimestamp) : null;
    return {
      name: sessionName,
      when: timestamp ? timestamp.toLocaleString() : null,
    };
  }, [lastSession]);

  if (!isOpen) {
    return null;
  }

  return (
    <EuiOverlayMask>
      <EuiModal onClose={onClose} initialFocus="[data-focus]">
        <EuiModalHeader>
          <EuiModalHeaderTitle>{copy.title}</EuiModalHeaderTitle>
        </EuiModalHeader>
        <EuiModalBody>
          <EuiText size="m">
            <p>{copy.prompt}</p>
          </EuiText>
          {sessionSummary && (
            <>
              <EuiSpacer size="m" />
              <div className="workspace-choice-summary">
                <FiClock />
                <div>
                  <strong>{sessionSummary.name}</strong>
                  {sessionSummary.when && (
                    <span>Last updated {sessionSummary.when}</span>
                  )}
                </div>
              </div>
            </>
          )}
          <EuiSpacer size="m" />
          <EuiText size="s" color="subdued">
            <p>{copy.hint}</p>
          </EuiText>
        </EuiModalBody>
        <EuiModalFooter>
          <EuiButtonEmpty onClick={onClose}>Cancel</EuiButtonEmpty>
          <EuiButton color="text" onClick={onStartNew}>
            {copy.newLabel}
          </EuiButton>
          <EuiButton fill onClick={onContinue} data-focus>
            {copy.continueLabel}
          </EuiButton>
        </EuiModalFooter>
      </EuiModal>
    </EuiOverlayMask>
  );
};

export default WorkspaceChoiceModal;
